// src/components/Faturas.js
import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebaseConfig';
import { collection, doc, getDoc, getDocs, query, orderBy, setDoc } from 'firebase/firestore';
// Import date-fns functions
import { format, addDays, differenceInDays, getDate, getMonth, getYear, setDate, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
// Import new icon for NF emission
import { Building, User, List, DollarSign, X, Download, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight, Filter, FileText, Loader2, RefreshCw, ExternalLink, Copy } from 'lucide-react'; // Added Copy for batch actions
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../styles/Faturas.css'; // Make sure this CSS file exists and includes styles for new elements
//import logo from '../assets/logosolucx.png';


const FINANCEIRO_EMAIL = 'financeiro@solucx.com.br';
const OMIE_PROXY_FUNCTION_URL = 'https://southamerica-east1-solucx-rh.cloudfunctions.net/omieProxy';
// const OMIE_PROXY_FUNCTION_URL = 'http://127.0.0.1:5001/solucx-rh/southamerica-east1/omieProxy'; // Local proxy for testing

// --- Helper Functions ---
const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const formatCount = (value) => (value == null || isNaN(value)) ? '0' : Number(value).toLocaleString('pt-BR');
const formatarContagem = (value) => {
    if (value == null || isNaN(value)) return '0';
    return Math.floor(Number(value)).toLocaleString('pt-BR');
};
const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    const digits = cnpj.replace(/\D/g, '');
    if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'); // CPF
    } else if (digits.length === 14) {
        return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5'); // CNPJ
    }
    return cnpj;
};
const formatDate = (d) => {
    try {
        const dateObj = d instanceof Date ? d : new Date(d);
        if (isNaN(dateObj.getTime())) throw new Error("Invalid date");
        return format(dateObj, 'dd/MM/yyyy');
    } catch (e) {
        console.warn("FormatDate error:", e, "Input:", d);
        return "Data inválida";
    }
};
const formatDateapi = (d) => {
    try {
        const dateObj = d instanceof Date ? d : new Date(d);
        if (isNaN(dateObj.getTime())) throw new Error("Invalid date");
        return format(dateObj, 'yyyy-MM-dd');
    } catch (e) {
         console.warn("FormatDateAPI error:", e, "Input:", d);
        return "Data inválida";
    }
};
const formatMonthYear = (d) => {
    try {
        const dateObj = d instanceof Date ? d : new Date(d);
        if (isNaN(dateObj.getTime())) throw new Error("Invalid date");
        return format(dateObj, 'MMMM/yyyy', { locale: ptBR }).replace(/^\w/, c => c.toUpperCase());
    } catch (e) {
        console.warn("FormatMonthYear error:", e, "Input:", d);
        return "Data inválida";
    }
}
const getOptions1 = (apitoken) => ({
    credentials: 'omit',
    headers: {
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:88.0) Gecko/20100101 Firefox/88.0',
        'Accept': 'application/json, text/plain, */*',
        'Accept-Language': 'pt-BR,pt;q=0.8,en-US;q=0.5,en;q=0.3',
        'x-solucx-api-key': apitoken,
        'x-solucx-referer': 'https://app.solucx.com.br/'
    },
    referrer: 'https://app.solucx.com.br/',
    method: 'GET',
    mode: 'cors'
});
const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

// Helper function to map Omie NFSe status codes to user-friendly descriptions
const mapOmieNFSeStatusToUserFriendly = (status) => {
    if (!status) return 'Desconhecida';
    switch (status.toUpperCase()) {
        case 'F': return 'Autorizada (Faturada)';
        case 'A': return 'Aguardando Aprovação'; // Hypothetical, check Omie docs
        case 'E': return 'Erro na Prefeitura';   // Hypothetical
        case 'C': return 'Cancelada';
        case 'P': return 'Processando na Prefeitura'; // Hypothetical
        // Add more statuses from Omie documentation as needed
        default: return `Status Omie: ${status}`;
    }
};
// --- End Helper Functions ---

// --- Firestore Persistence Helper ---
const persistNFeDataToFirestore = async (invoiceDataWithNFe, empresaIdFromState) => {
    // ATENÇÃO: 'monthNames' precisa estar acessível aqui se for usá-lo para derivar docId.
    // Se 'monthNames' estiver definido apenas dentro do componente Faturas, passe-o como argumento
    // ou defina-o em um escopo global acessível por esta função.
    // Para este exemplo, vamos assumir que 'monthNames' está acessível globalmente ou não é necessário
    // porque 'invoiceDataWithNFe.id' (o docId 'yyyy-MM') já estará presente para faturas lidas do DB.

    if (!invoiceDataWithNFe || invoiceDataWithNFe.type !== 'Fechada') {
        if (invoiceDataWithNFe && invoiceDataWithNFe.type === 'Setup') {
            console.warn(`Persistência de NF para Setup (${invoiceDataWithNFe.clientName} - ${invoiceDataWithNFe.period}) não implementada nesta função. Requer atualização no documento do Cliente/productsData.`);
        }
        return;
    }

    let docIdToSave = invoiceDataWithNFe.id; // 'id' é o ID do documento Firestore (ex: "2024-05")

    // Se a fatura é 'Fechada' e foi calculada (não veio do DB), ela pode não ter 'id' ainda.
    // Neste caso, o 'monthLabel' é usado para derivar o docId.
    if (!docIdToSave && invoiceDataWithNFe.monthLabel) {
        try {
            const dateParts = invoiceDataWithNFe.monthLabel.split('/'); // Ex: "Maio/2025"
            const year = parseInt(dateParts[1], 10);
            // Certifique-se que 'monthNames' está definido e acessível aqui
            // const globalMonthNames = ['Janeiro', 'Fevereiro', ...]; (Defina se não estiver global)
            const monthIndex = monthNames.findIndex(m => m.toLowerCase() === dateParts[0].toLowerCase()); // Usa monthNames do escopo superior
            if (monthIndex !== -1 && !isNaN(year)) {
                docIdToSave = format(new Date(year, monthIndex, 1), 'yyyy-MM');
            } else {
                console.error("persistNFeDataToFirestore: Não foi possível derivar docIdToSave de monthLabel:", invoiceDataWithNFe.monthLabel);
                return;
            }
        } catch (e) {
            console.error("persistNFeDataToFirestore: Erro ao parsear monthLabel:", e);
            return;
        }
    }

    if (docIdToSave && empresaIdFromState && invoiceDataWithNFe.clientId) {
        const faturaRef = doc(db, 'Empresas', empresaIdFromState, 'Clientes', invoiceDataWithNFe.clientId, 'Faturas', docIdToSave);

        const nfFieldsToPersist = {
            omie_nCodOS: invoiceDataWithNFe.omie_nCodOS || null,
            nCodOmieNF: invoiceDataWithNFe.nCodOmieNF || null,
            nNumeroNFSe: invoiceDataWithNFe.nNumeroNFSe || null,
            cCodigoVerifNFSe: invoiceDataWithNFe.cCodigoVerifNFSe || null,
            nfStatus: invoiceDataWithNFe.nfStatus || null,
            nfSituacao: invoiceDataWithNFe.nfSituacao || null,
            nfStatusOmie: invoiceDataWithNFe.nfStatusOmie || null,
            lastUpdatedNF: new Date().toISOString() // Timestamp da última atualização dos dados da NF
        };

        try {
            console.log(`Firestore: Tentando salvar/mesclar dados da NF para Cliente ${invoiceDataWithNFe.clientId}, Fatura ${docIdToSave}:`, nfFieldsToPersist);
            // Usar setDoc com merge:true para atualizar ou criar se não existir, mesclando os campos.
            await setDoc(faturaRef, nfFieldsToPersist, { merge: true });
            console.log(`Firestore: Dados da NF para fatura ${docIdToSave} salvos/mesclados com sucesso.`);
        } catch (error) {
            console.error(`Firestore: Erro ao salvar/mesclar dados da NF para fatura ${docIdToSave}:`, error);
        }
    } else {
        if (invoiceDataWithNFe.type === 'Fechada') { // Logar apenas para 'Fechada' se falhar em obter IDs
            console.warn("persistNFeDataToFirestore: Informações insuficientes para salvar no Firestore (docId, empresaId, clientId). Fatura:", invoiceDataWithNFe);
        }
    }
};
// --- End Firestore Persistence Helper ---

const Faturas = () => {
    const [empresaId, setEmpresaId] = useState(null);
    const [issuerInfo, setIssuerInfo] = useState({});
    const [clients, setClients] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedInv, setSelectedInv] = useState(null);
    const user = auth.currentUser;
    const modalRef = useRef(null);

    const [emittingNFFor, setEmittingNFFor] = useState(null); // Stores ID of invoice being processed for NF emission
    const [emissionStatus, setEmissionStatus] = useState({ status: 'idle', message: '' }); // Overall NF emission status
    const [checkingNFStatusFor, setCheckingNFStatusFor] = useState(null); // invoiceId being checked
    const [nfCheckStatus, setNfCheckStatus] = useState({ status: 'idle', message: ''});

    const [filterClient, setFilterClient] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const initialMonth = formatMonthYear(new Date());
    const [filterMonth, setFilterMonth] = useState(initialMonth);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // State for batch actions
    const [selectedInvoices, setSelectedInvoices] = useState(new Set()); // Stores IDs of selected invoices

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const uSnap = await getDoc(doc(db, 'users', user.uid));
                const empId = uSnap.data()?.empresa_id;
                setEmpresaId(empId);
                if (empId) {
                    const infColRef = collection(db, 'Empresas', empId, 'infempresa');
                    const infSnapCol = await getDocs(infColRef);
                    if (!infSnapCol.empty) {
                        const primeiroDoc = infSnapCol.docs[0].data();
                        setIssuerInfo({
                            empnome: primeiroDoc.empnome ?? '',
                            empcnpj: primeiroDoc.empcnpj ?? ''
                        });
                    } else {
                        console.warn(`Nenhum documento encontrado em Empresas/${empId}/infempresa`);
                    }
                }
            } catch (error) {
                console.error("Erro ao buscar dados da empresa:", error);
                setLoading(false);
            }
        })();
    }, [user]);

    useEffect(() => {
        if (!empresaId || !user) return;
        (async () => {
            try {
                const col = collection(db, 'Empresas', empresaId, 'Clientes');
                const snap = await getDocs(query(col, orderBy('name')));
                let list = snap.docs.map(d => ({
                    id: d.id,
                    ...d.data(),
                    rawCnpj: d.data().cnpj?.replace(/\D/g, ''),
                }));
                const uSnap = await getDoc(doc(db, 'users', user.uid));
                const dep = uSnap.data()?.departamento;
                if (dep === 'CS' && user.email) list = list.filter(c => c.responsavelCS === user.email);
                if (dep === 'Comercial' && user.email) list = list.filter(c => c.responsavelComercial === user.email);
                setClients(list);
            } catch (error) {
                console.error("Erro ao buscar clientes:", error);
                setLoading(false);
            }
        })();
    }, [empresaId, user]);

    useEffect(() => {
        if (!issuerInfo.empnome || clients.length === 0 || !empresaId) {
             if (!loading && clients.length === 0 && empresaId && issuerInfo.empnome) {
                 setLoading(false);
             }
            return;
        }

        (async () => {
            const today = new Date();
            const currentYear = today.getFullYear();
            const currentMonthIndex = today.getMonth();
            const invs = [];

            try {
                for (const client of clients) {
                    // Gerar ID único para a fatura (usado na seleção)
                    const generateInvoiceId = (invData) => `${invData.clientId}-${invData.monthLabel || invData.period.replace(/\s*→\s*/, '_')}-${invData.type}`;

                    for (const prod of client.produtos || []) {
                        const pd = client.productsData?.[prod] || {};
                        const prefix = prod.toLowerCase().split(' ')[0].substring(0, 4);
                        const setupFlag = pd[`${prefix}_setup`];
                        const setupDateStr = pd[`${prefix}_dataEmissaoSetup`];
                        const setupValue = parseFloat(pd[`${prefix}_valorSetup`] || 0);

                        if (setupFlag === 'Sim' && setupDateStr && setupValue > 0) {
                            try {
                                const setupDate = new Date(setupDateStr + 'T00:00:00');
                                if (!isNaN(setupDate)) {
                                    const invSetup = {
                                        clientId: client.id, clientName: client.name, clientCnpj: formatCNPJ(client.rawCnpj) || '',
                                        clientEndereco: client.endereco || '', period: `${formatDate(setupDate)}`,
                                        monthLabel: formatMonthYear(setupDate), type: 'Setup', total: setupValue,
                                        details: [{ desc: `${prod} – Taxa de Setup`, value: formatCurrency(setupValue) }],
                                        consumido: 0, contratado: 0, excedenteExiste: false, valorTicket: 0,
                                        clientRef: client,
                                        omie_nCodOS: pd.omie_nCodOS || null,
                                        nCodOmieNF: pd.nCodOmieNF || null,
                                        nNumeroNFSe: pd.nNumeroNFSe || null,
                                        cCodigoVerifNFSe: pd.cCodigoVerifNFSe || null,
                                        nfStatus: pd.nfStatus || null,
                                        nfSituacao: pd.nfSituacao || null, // Situação da prefeitura
                                        nfStatusOmie: pd.nfStatusOmie || null, // Status da Omie (cStatusNFSe)
                                    };
                                    invSetup.uniqueId = generateInvoiceId(invSetup);
                                    invs.push(invSetup);
                                } else {
                                     console.warn(`Setup date invalid for ${client.name}, ${prod}: ${setupDateStr}`)
                                }
                            } catch (e) {
                                console.error(`Erro no setup de ${client.name}, ${prod}:`, e);
                            }
                        }
                    }

                    let firstBillMonthIndex = null;
                    let firstBillYear = currentYear;
                     for (const prod of client.produtos || []) {
                        const pd = client.productsData?.[prod] || {};
                        const prefix = prod.toLowerCase().split(' ')[0].substring(0, 4);
                        const compStr = pd[`${prefix}_competenciaPrimeiroFaturamento`];
                        if (!compStr) continue;
                        let monthIdx, yearNum;
                        if (compStr.includes('/')) {
                            const [rawMM, rawYYYY] = compStr.split('/').map(s => s.trim());
                            monthIdx = parseInt(rawMM, 10) - 1;
                            yearNum = parseInt(rawYYYY, 10);
                        } else if (compStr.includes('-')) {
                            const [rawYYYY, rawMM] = compStr.split('-').map(s => s.trim());
                            monthIdx = parseInt(rawMM, 10) - 1;
                            yearNum = parseInt(rawYYYY, 10);
                        } else {
                             console.warn(`Formato de competência não reconhecido para ${client.name}, produto ${prod}: "${compStr}"`);
                             continue;
                        }

                        if (monthIdx >= 0 && monthIdx <= 11 && yearNum > 1970) {
                            if (firstBillMonthIndex === null || yearNum < firstBillYear || (yearNum === firstBillYear && monthIdx < firstBillMonthIndex)) {
                                firstBillMonthIndex = monthIdx;
                                firstBillYear = yearNum;
                            }
                        } else { console.warn(`Competência inválida para ${client.name}, produto ${prod}: "${compStr}"`); }
                    }

                    if (firstBillMonthIndex !== null) {
                        const competencies = [];
                        let y = firstBillYear, m = firstBillMonthIndex;
                        while (y < currentYear || (y === currentYear && m <= currentMonthIndex)) {
                            competencies.push({ year: y, month: m });
                            m++;
                            if (m > 11) { m = 0; y++; }
                        }

                        for (const { year, month } of competencies) {
                            const dateForInvoice = new Date(year, month, 1);
                            const isClosed = !(year === currentYear && month === currentMonthIndex);
                            const invoiceDocId = format(dateForInvoice, 'yyyy-MM');
                            let invoiceData = null;

                            if (isClosed && empresaId && client.id) {
                                try {
                                    const faturaRef = doc(db, 'Empresas', empresaId, 'Clientes', client.id, 'Faturas', invoiceDocId);
                                    const docSnap = await getDoc(faturaRef);

                                    if (docSnap.exists()) {
                                        invoiceData = { ...docSnap.data(), id: docSnap.id };
                                        console.log(`Fatura Fechada lida do Firestore para ${client.name} - ${invoiceDocId}`);
                                    } else {
                                        console.log(`Fatura Fechada ${invoiceDocId} para ${client.name} não encontrada no DB, calculando...`);
                                        invoiceData = await computeInvoice(client, dateForInvoice, isClosed);
                                    }
                                } catch (error) {
                                    console.error(`Erro ao LER fatura fechada ${invoiceDocId} para ${client.name} do Firestore:`, error);
                                    invoiceData = await computeInvoice(client, dateForInvoice, isClosed);
                                }
                            } else {
                                invoiceData = await computeInvoice(client, dateForInvoice, isClosed);
                            }

                            if (invoiceData?.details?.length) {
                                if (invoiceData.type === 'Fechada' && !invoiceData.id && empresaId && client.id) {
                                    try {
                                        const faturaRef = doc(db, 'Empresas', empresaId, 'Clientes', client.id, 'Faturas', invoiceDocId);
                                        const { clientRef, ...dataToSave } = invoiceData; // clientRef não deve ser salvo
                                        await setDoc(faturaRef, {
                                            ...dataToSave,
                                            savedAt: new Date()
                                        });
                                        console.log(`Fatura Fechada SALVA no Firestore para ${client.name} - ${invoiceDocId}`);
                                    } catch (error) {
                                        console.error(`Erro ao SALVAR fatura fechada ${invoiceDocId} para ${client.name} no Firestore:`, error);
                                    }
                                }
                                invoiceData.clientRef = client;
                                invoiceData.omie_nCodOS = invoiceData.omie_nCodOS || null;
                                invoiceData.nCodOmieNF = invoiceData.nCodOmieNF || null;
                                invoiceData.nNumeroNFSe = invoiceData.nNumeroNFSe || null;
                                invoiceData.cCodigoVerifNFSe = invoiceData.cCodigoVerifNFSe || null;
                                invoiceData.nfStatus = invoiceData.nfStatus || ( (invoiceData.type === 'Fechada' || invoiceData.type === 'Setup') ? 'Não emitida' : null);
                                invoiceData.nfStatus = invoiceData.nfStatus || null;
                                invoiceData.nfSituacao = invoiceData.nfSituacao || null;
                                invoiceData.nfStatusOmie = invoiceData.nfStatusOmie || null;
                                invoiceData.uniqueId = generateInvoiceId(invoiceData);
                                invs.push(invoiceData);
                            }
                        }
                    }
                }

                invs.sort((a, b) => {
                    if (a.clientName < b.clientName) return -1;
                    if (a.clientName > b.clientName) return 1;
                    try {
                        const getDatePart = (p) => p.includes('→') ? p.split('→')[0].trim() : p;
                        const parseDMY = (str) => {
                            const parts = str.split('/');
                            const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
                            return new Date(year, parts[1] - 1, parts[0]);
                         };
                        const dateA = parseDMY(getDatePart(a.period));
                        const dateB = parseDMY(getDatePart(b.period));
                        if (!isNaN(dateA) && !isNaN(dateB)) return dateB - dateA;
                    } catch (e) { console.warn("Sorting date parse error:", e) }
                    return 0;
                });
                setInvoices(invs.filter(inv => inv?.details?.length > 0));

            } catch (error) {
                console.error("Erro GERAL ao gerar/buscar faturas:", error);
            } finally {
                setLoading(false);
            }
        })();
    }, [issuerInfo, clients, empresaId]);

    const computeInvoice = async (client, periodDate, isClosed) => {
        // ... (computeInvoice logic remains largely the same as provided in the original file)
        const today = new Date();
        let start, end;
        let calculationEndDate = today;

        if (isClosed) {
            start = new Date(periodDate.getFullYear(), periodDate.getMonth(), 1);
            end = new Date(periodDate.getFullYear(), periodDate.getMonth() + 1, 0);
            calculationEndDate = end;
        } else {
            start = new Date(periodDate.getFullYear(), periodDate.getMonth(), 1);
            end = today;
            calculationEndDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() -1 );
             if (today.getDate() === 1) {
                 console.log(`Pulando cálculo provisório para ${client.name} no dia 1.`);
                 return null;
             }
             if (calculationEndDate < start) {
                 calculationEndDate = start;
                 console.log(`Período provisório ajustado: Data final API < Data inicial para ${client.name}.`);
             }
        }

        const dataInicioStr = formatDate(start);
        const dataInicioAPI = formatDateapi(start);
        const dataFimStrDisplay = formatDate(end);
        const dataFimAPI = formatDateapi(calculationEndDate);

        let invoiceTotal = 0;
        const invoiceDetails = [];
        let allProductsData = {
            consumidoTotal: 0, contratadoTotal: 0, valorTicketTotal: 0,
            valorAdicionalTotalCalculado: 0, excedenteExiste: false, produtosComExcedente: []
        };
        let cxVolumeContratado = 0;
        let cxvalorTicket = 0;

        try {
            for (const prod of client.produtos || []) {
                const pd = client.productsData?.[prod] || {};
                const prefix = prod.toLowerCase().split(' ')[0].substring(0, 4);

                const formatoContratacao = pd[`${prefix}_formatoContratacao`];
                const volumeContratado = parseFloat(pd[`${prefix}_volumeResposta`] || 0);
                const valorTicket = parseFloat(pd[`${prefix}_valorTicket`] || 0);
                const tipoPrecificacao = pd[`${prefix}_tipoPrecificacao`];
                const valorAdicionalUnitario = parseFloat(pd[`${prefix}_valorRespostaAdicional`] || 0);
                const isTagSense = pd[`${prefix}_TagSense`] === 'Sim';
                const valorTagSense = parseFloat(pd[`${prefix}_valorTagSense`] || 0);
                const tokenAPI = client.token;
                const apiKeyAPI = client.apitoken;

                let consumoApi = 0;
                let erroApi = false;

                if (prod === 'CX Platform' && tokenAPI && apiKeyAPI && dataInicioAPI !== "Data inválida" && dataFimAPI !== "Data inválida") {
                     cxVolumeContratado = volumeContratado;
                     cxvalorTicket = valorTicket;
                     let apiUrl = '';
                     if (formatoContratacao === 'Resposta') {
                        apiUrl = `https://api-cxp.solucx.com.br/transaction/count?token=${tokenAPI}&rows_limit=1&page=1&show_data=0&date_rating_from=${dataInicioAPI}&date_rating_to=${dataFimAPI}&type=1`;
                    } else if (formatoContratacao === 'Disparo') {
                        apiUrl = `https://api-cxp.solucx.com.br/transaction/count?token=${tokenAPI}&rows_limit=1&page=1&show_data=0&date_disparo_from=${dataInicioAPI}&date_disparo_to=${dataFimAPI}&type=1&attempt_status=sended`;
                    }

                    if (apiUrl) {
                        try {
                            const response = await fetch(apiUrl, getOptions1(apiKeyAPI));
                            if (!response.ok) throw new Error(`API Error ${response.status}`);
                            const json = await response.json();
                            consumoApi = formatoContratacao === 'Resposta'
                                        ? parseFloat(json.transactionAmount || 0)
                                        : parseFloat(json.surveyAmount || 0);
                             if (isNaN(consumoApi)) consumoApi = 0;
                        } catch (error) {
                            console.error(`Erro ao buscar API Solucx para ${client.name} / ${prod}: ${error}`);
                            invoiceDetails.push({ desc: `${prod} - Erro ao buscar consumo API`, value: 'Erro', isError: true });
                            erroApi = true;
                        }
                    }
                } else if (prod === 'CX Platform' && (!tokenAPI || !apiKeyAPI)) {
                     console.warn(`API token/key ausente para ${client.name} / ${prod}. Consumo API não buscado.`);
                     invoiceDetails.push({ desc: `${prod} - Token/API Key Ausente`, value: 'N/A', isWarning: true });
                }

                let valorProduto = 0;
                let valorExcedenteProduto = 0;
                let contagemExcedenteProduto = 0;
                let consumoConsiderado = erroApi ? 0 : consumoApi;

                if (valorTicket > 0) {
                  valorProduto += valorTicket;
                  invoiceDetails.push({ desc: `${prod} – Ticket Mínimo Contratado`, value: formatCurrency(valorTicket) });
                }

                if (prod === "CX Platform") {
                     const consumoLabel = `Consumo ${formatoContratacao}`;
                     const consumoValue = erroApi ? 'Erro API' : formatarContagem(consumoConsiderado);
                     const limiteInfo = volumeContratado > 0 ? ` / ${formatarContagem(volumeContratado)} contratado` : '';
                     const status = erroApi ? 'error' : (volumeContratado > 0 && consumoConsiderado > volumeContratado ? 'warning' : 'ok');
                     const statusText = erroApi ? '' : (volumeContratado > 0 && consumoConsiderado > volumeContratado ? ' - Excedeu limite!' : (volumeContratado > 0 ? ' - Dentro do limite' : ''));
                     invoiceDetails.push({
                         desc: `${prod} – ${consumoLabel}${limiteInfo}`,
                         value: `${consumoValue}${statusText}`,
                         isInfo: true,
                         status: status
                     });
                 }

                if (!erroApi && volumeContratado > 0 && consumoConsiderado > volumeContratado) {
                    contagemExcedenteProduto = consumoConsiderado - volumeContratado;
                    allProductsData.excedenteExiste = true;
                    if (valorAdicionalUnitario > 0) {
                         if (tipoPrecificacao === 'TICKET MÍNIMO' || tipoPrecificacao === 'PRECIFICAÇÃO FIXA' || tipoPrecificacao === 'VOLUME') {
                             valorExcedenteProduto = contagemExcedenteProduto * valorAdicionalUnitario;
                             invoiceDetails.push({
                                 desc: `${prod} – Custo Excedente (${formatarContagem(contagemExcedenteProduto)} x ${formatCurrency(valorAdicionalUnitario)})`,
                                 value: formatCurrency(valorExcedenteProduto),
                                 isExcedente: true
                             });
                             valorProduto += valorExcedenteProduto;
                         } else {
                              invoiceDetails.push({ desc: `${prod} – Excedente (${formatarContagem(contagemExcedenteProduto)}) / Sem Custo Adicional (Tipo Prec: ${tipoPrecificacao})`, value: formatCurrency(0), isWarning: true });
                         }
                    } else {
                         invoiceDetails.push({ desc: `${prod} – Excedente (${formatarContagem(contagemExcedenteProduto)})`, value: 'Sem custo adicional', isExcedente: true });
                    }
                    allProductsData.produtosComExcedente.push({
                        nome: prod, contagem: contagemExcedenteProduto, valorCobrado: valorExcedenteProduto, valorUnitario: valorAdicionalUnitario
                    });
                }

                if (isTagSense && valorTagSense > 0) {
                    valorProduto += valorTagSense;
                    invoiceDetails.push({ desc: `${prod} – Tag Sense`, value: formatCurrency(valorTagSense) });
                }
                invoiceTotal += valorProduto;
                allProductsData.consumidoTotal += consumoConsiderado;
                allProductsData.contratadoTotal += volumeContratado;
                allProductsData.valorTicketTotal += valorTicket;
                allProductsData.valorAdicionalTotalCalculado += valorExcedenteProduto;
            }

             if (invoiceDetails.length === 0 || (invoiceTotal === 0 && !invoiceDetails.some(d => d.isError || d.isWarning))) {
                 console.log(`Fatura zerada ou sem itens válidos para ${client.name} no período ${dataInicioStr} - ${dataFimStrDisplay}, pulando.`);
                 return null;
             }

            return {
                clientId: client.id, clientName: client.name, clientCnpj: formatCNPJ(client.rawCnpj) || '', clientEndereco: client.endereco || '',
                period: `${dataInicioStr} → ${dataFimStrDisplay}`, monthLabel: formatMonthYear(start),
                type: isClosed ? 'Fechada' : 'Provisória', total: invoiceTotal, details: invoiceDetails,
                cx_VolumeContratado: cxVolumeContratado, cx_ticket: cxvalorTicket,
                consumido: allProductsData.consumidoTotal, contratado: allProductsData.contratadoTotal,
                valorTicket: allProductsData.valorTicketTotal, excedenteExiste: allProductsData.excedenteExiste,
                contagemExcedenteTotal: allProductsData.produtosComExcedente.reduce((sum, p) => sum + p.contagem, 0),
                valorExcedenteTotal: allProductsData.valorAdicionalTotalCalculado,
                produtosComExcedente: allProductsData.produtosComExcedente,
                // NF-e fields are added from DB or updated after emission
            };
        } catch (error) {
            console.error(`Erro fatal ao calcular fatura para ${client.name} (${formatMonthYear(periodDate)}):`, error);
            return {
                clientId: client.id, clientName: client.name, clientCnpj: formatCNPJ(client.rawCnpj) || '', clientEndereco: client.endereco || '',
                period: `${formatDate(start)} → ${formatDate(end)}`, monthLabel: formatMonthYear(start), type: isClosed ? 'Erro' : 'Erro (Prov.)', total: 0,
                details: [{ desc: "Erro geral no cálculo da fatura.", value: 'Verificar console', isError: true }],
                consumido: 0, contratado: 0, valorTicket: 0, excedenteExiste: false, contagemExcedenteTotal: 0, valorExcedenteTotal: 0, produtosComExcedente: [],
                clientRef: client // Adicionado para consistência, embora não usado em erro
            };
        }
    };


    const downloadPDF = async () => {
        // ... (downloadPDF logic remains the same as provided in the original file)
        if (!modalRef.current || !selectedInv) return;
        const original = modalRef.current;
        const clone = original.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.left = '-9999px';
        clone.style.top = '0px';
        clone.style.zIndex = '-1';
        clone.style.width = '600px';
        clone.style.height = 'auto';
        clone.style.maxHeight = 'none';
        clone.style.overflow = 'visible';
        clone.style.background = '#fff';
        clone.style.boxShadow = 'none';
        clone.style.opacity = '1';
        clone.style.display = 'block';

        const act = clone.querySelector('.modal-actions');
        const closeBtn = clone.querySelector('.modal-close');
        if (act) act.style.display = 'none';
        if (closeBtn) closeBtn.remove();
        clone.querySelectorAll('.table-container').forEach(el => {
             el.style.maxHeight = 'none';
             el.style.overflow = 'visible';
        });
         const logoEl = clone.querySelector('.modal-logo');
         if (logoEl) {
            logoEl.style.maxWidth = '150px';
            logoEl.style.display = 'block';
            logoEl.style.margin = '20px auto 10px auto';
         } else {
             const newLogoEl = document.createElement('img');
             newLogoEl.src = logo;
             newLogoEl.style.maxWidth = '150px';
             newLogoEl.style.display = 'block';
             newLogoEl.style.margin = '20px auto 10px auto';
             clone.prepend(newLogoEl);
         }
        const titleSection = clone.querySelector('.modal-title');
        if (titleSection) {
            titleSection.style.textAlign = 'center';
            titleSection.style.marginBottom = '20px';
            const titleH2 = titleSection.querySelector('h2');
            if(titleH2) {
                titleH2.style.fontSize = '1.4em';
                titleH2.style.margin = '5px 0';
            }
            const subtitleSpan = titleSection.querySelector('.modal-subtitle');
             if(subtitleSpan) {
                 subtitleSpan.style.fontSize = '1em';
                 subtitleSpan.style.color = '#555';
             }
        }
        document.body.appendChild(clone);
        await new Promise(resolve => setTimeout(resolve, 200)); // Short delay for rendering
        try {
             const canvas = await html2canvas(clone, {
                 scale: 2, useCORS: true, logging: false,
                 width: clone.scrollWidth, height: clone.scrollHeight,
                 windowWidth: clone.scrollWidth, windowHeight: clone.scrollHeight
             });
            if (document.body.contains(clone)) document.body.removeChild(clone);

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 40; // Page margin
            const imgProps = pdf.getImageProperties(imgData);
            const usableWidth = pdfWidth - margin * 2;
            const imgRatio = imgProps.width / imgProps.height;
            let imgWidth = usableWidth;
            let imgHeight = usableWidth / imgRatio;
            let pageHeightAvailable = pdfHeight - margin * 2;

             if (imgHeight > pageHeightAvailable) { // If content is taller than one page
                imgHeight = pageHeightAvailable; // Scale down to fit height
                imgWidth = imgHeight * imgRatio; // Adjust width proportionally
                 console.warn("PDF content scaled down to fit page height initially.")
             }

            let heightLeft = imgProps.height * (imgWidth / imgProps.width); // Total height of the image at scaled width
            let currentPosition = margin;

            // Add first page/part of image
            pdf.addImage(imgData, 'PNG', margin, currentPosition, imgWidth, imgHeight);
            heightLeft -= pageHeightAvailable; // Subtract height of first page

            // Add subsequent pages if needed
            while (heightLeft > 0) {
                pdf.addPage();
                currentPosition = margin - (imgProps.height * (imgWidth / imgProps.width) - heightLeft); // Calculate Y offset for this part of the image
                pdf.addImage(imgData, 'PNG', margin, currentPosition, imgWidth, imgProps.height * (imgWidth / imgProps.width) );
                heightLeft -= pageHeightAvailable;
            }

            pdf.save(`fatura_${selectedInv.clientName.replace(/[^a-z0-9]/gi, '_')}_${(selectedInv.monthLabel || selectedInv.period).replace(/[^a-z0-9]/gi, '-')}.pdf`);
        } catch (err) {
            console.error('Erro ao gerar PDF:', err);
            if (document.body.contains(clone)) document.body.removeChild(clone);
            alert('Ocorreu um erro ao gerar o PDF. Verifique o console para detalhes.');
        }
    };


    const getFilteredInvoices = () => {
        return invoices.filter(inv => {
            if (!inv) return false;
            const clientMatch = !filterClient || (inv.clientName && inv.clientName.toLowerCase().includes(filterClient.toLowerCase()));
            const statusMatch = !filterStatus || (inv.type && inv.type === filterStatus);
            const monthMatch = !filterMonth || (inv.monthLabel && inv.monthLabel === filterMonth);
            return clientMatch && statusMatch && monthMatch;
        });
    };

    const filteredInvoices = getFilteredInvoices();
    const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);

    const handleFilterChange = () => { setCurrentPage(1); };
    const handleClientFilterChange = (e) => { setFilterClient(e.target.value); handleFilterChange(); };
    const handleStatusFilterChange = (e) => { setFilterStatus(e.target.value); handleFilterChange(); };
    const handleMonthFilterChange = (e) => { setFilterMonth(e.target.value); handleFilterChange(); };

    const uniqueMonths = [...new Set(invoices.map(inv => inv.monthLabel).filter(Boolean))]
        .sort((a, b) => {
             try {
                 const parseMonthYear = (myStr) => {
                    const [monthName, year] = myStr.split('/');
                    const monthIndex = monthNames.indexOf(monthName);
                    if (monthIndex === -1 || !year || isNaN(parseInt(year))) return new Date(9999, 0, 1); // Sort invalid dates last
                    return new Date(parseInt(year), monthIndex, 1);
                 };
                 const dateA = parseMonthYear(a);
                 const dateB = parseMonthYear(b);
                 return dateA - dateB; // Sort ascending by date
             } catch (e) {
                 // Fallback for any unexpected format
                 return a.localeCompare(b);
             }
        });

    const handleEmitirNF = async (invoice) => {
        const invoiceUniqueId = invoice.uniqueId; // Use o ID único da fatura
        setEmittingNFFor(invoiceUniqueId);
        setEmissionStatus({ status: 'loading', message: 'Iniciando emissão da OS...' });

        const client = invoice.clientRef;
        if (!client) {
            const errorMsg = "Erro: Dados completos do cliente não encontrados na fatura.";
            alert(errorMsg);
            setEmissionStatus({ status: 'error', message: errorMsg});
            setEmittingNFFor(null);
            return;
        }

        let nCodOSCriado = invoice.omie_nCodOS || null; // Use existing OS code if retrying
        let cCodIntOSCriado = ""; // Será preenchido se uma nova OS for criada ou obtido se já existir
        let nCodCli = null;

        // Se já temos o nCodOSCriado, pulamos a busca do cliente e criação da OS, e tentamos faturar diretamente
        // Isso é útil se a criação da OS funcionou, mas o faturamento falhou anteriormente.
        if (!nCodOSCriado) {
            try {
                setEmissionStatus({ status: 'loading', message: 'Buscando código do cliente Omie...' });
                const rawCnpj = client.rawCnpj;
                if (!rawCnpj || (rawCnpj.length !== 11 && rawCnpj.length !== 14)) {
                    throw new Error(`CNPJ/CPF '${rawCnpj || ''}' inválido ou ausente no cliente ${client.name}.`);
                }
                const responseGetClient = await fetch(OMIE_PROXY_FUNCTION_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: "getClientCode", payload: { cnpj_cpf: rawCnpj } })
                });
                if (!responseGetClient.ok) {
                    let errorData; try { errorData = await responseGetClient.json(); } catch (e) { const errorText = await responseGetClient.text(); console.error("Raw error from getClientCode:", errorText); throw new Error(errorText.substring(0,200) || `Erro ${responseGetClient.status}`);}
                    throw new Error(errorData.message || errorData.error || `Erro ${responseGetClient.status}`);
                }
                const resultGetClient = await responseGetClient.json();
                if (resultGetClient.faultstring) { throw new Error(`Omie (Cliente): ${resultGetClient.faultstring}`); }
                if (resultGetClient.total_de_registros === 0 || !resultGetClient.clientes_cadastro_resumido?.[0]?.codigo_cliente) {
                    throw new Error(`Cliente com CNPJ/CPF ${formatCNPJ(rawCnpj)} não encontrado na Omie.`);
                }
                nCodCli = resultGetClient.clientes_cadastro_resumido[0].codigo_cliente;
                console.log(`nCodCli encontrado: ${nCodCli}`);

            } catch (error) {
                console.error("Erro ao buscar nCodCli via proxy:", error);
                const errorMsg = `Erro ao buscar cliente na Omie: ${error.message}`;
                alert(errorMsg);
                setEmissionStatus({ status: 'error', message: `Falha (Cliente): ${error.message.substring(0, 100)}...` });
                setEmittingNFFor(null);
                return;
            }

            try {
                setEmissionStatus({ status: 'loading', message: `Cliente ${nCodCli} OK. Incluindo OS...` });
                const today = new Date();
                const todayFormatted = format(today, 'dd/MM/yyyy');
                const firstProductName = client.produtos && client.produtos.length > 0 ? client.produtos[0] : null;
                const productDataForNF = firstProductName ? (client.productsData?.[firstProductName] || {}) : {};
                const condicaoTipo = productDataForNF.cx_condicaoPagamentoTipo || client.cx_condicaoPagamentoTipo;
                const condicaoValorStr = productDataForNF.cx_condicaoPagamentoValor || client.cx_condicaoPagamentoValor;
                const formaPagamento = productDataForNF.cx_formaPagamento || client.cx_formaPagamento;
                let emailsNFSource = productDataForNF.cx_emailsNF || client.cx_emailsNF;
                const condicaoValor = parseInt(condicaoValorStr || '0', 10);
                let cCodParc = 'A01'; let diasParaVencimento = 1;
                if (condicaoTipo === "X Dias após a emissão da NF" && condicaoValor > 0) { diasParaVencimento = condicaoValor; cCodParc = `A${String(diasParaVencimento).padStart(2, '0')}`; }
                else if (condicaoTipo === "Todo dia X" && condicaoValor >= 1 && condicaoValor <= 31) {
                    const diaVencimentoAlvo = condicaoValor; let dataVencimentoCalculada = setDate(startOfDay(today), diaVencimentoAlvo);
                    if (dataVencimentoCalculada <= startOfDay(today)) { dataVencimentoCalculada = setDate(new Date(getYear(today), getMonth(today) + 1, diaVencimentoAlvo), diaVencimentoAlvo); }
                    diasParaVencimento = differenceInDays(dataVencimentoCalculada, startOfDay(today)); diasParaVencimento = Math.max(diasParaVencimento, 0); cCodParc = `A${String(diasParaVencimento).padStart(2, '0')}`;
                } else { console.warn(`Condição de pagamento ('${condicaoTipo}') não reconhecida ou valor ('${condicaoValorStr}') inválido para ${client.name}. Usando padrão A01.`); }
                const dataVencimento = addDays(startOfDay(today), diasParaVencimento); const dataVencimentoFormatted = format(dataVencimento, 'dd/MM/yyyy');
                const cEnvBoleto = (formaPagamento === "Boleto") ? "S" : "N";
                let emailsList = [];
                if (Array.isArray(emailsNFSource)) emailsList = [...emailsNFSource];
                else if (typeof emailsNFSource === 'string' && emailsNFSource.trim() !== '') { emailsList = emailsNFSource.split(',').map(e => e.trim()).filter(e => e); }
                if (!emailsList.includes(FINANCEIRO_EMAIL)) emailsList.push(FINANCEIRO_EMAIL);
                const cEnviarPara = emailsList.join(',');
                cCodIntOSCriado = `SOLUCX-${client.id.substring(0,5)}-${invoice.monthLabel?.replace('/','') || invoice.type.toUpperCase()}-${Date.now().toString().slice(-5)}`;
                const dadosAdicionais = `OS via API Solucx Faturamento | Ref: ${invoice.monthLabel || invoice.type} | Vencimento: ${dataVencimentoFormatted} | Nosso Cod Int: ${cCodIntOSCriado}`;
                const impostoFederalValor = invoice.total * 0.1345; const impostoMunicipalValor = invoice.total * 0.0211;
                const descricaoServico = `PRESTAÇÃO DE SERVIÇOS: Licenciamento Ou Cessão De Direito De Uso De Programas De Computação. | Trib. federal: ${formatCurrency(impostoFederalValor)} (13,45%), trib. municipal ${formatCurrency(impostoMunicipalValor)} (2,11%) - IBPT.`;
                const osParamPayload = {
                    Cabecalho: { cCodIntOS: cCodIntOSCriado, cCodParc: cCodParc, cEtapa: "50", dDtPrevisao: todayFormatted, nCodCli: nCodCli, nQtdeParc: 1 },
                    Departamentos: [], Email: { cEnvBoleto: cEnvBoleto, cEnvLink: "S", cEnviarPara: cEnviarPara },
                    InformacoesAdicionais: { cCidPrestServ: "SAO JOSE DOS CAMPOS (SP)", cCodCateg: "1.01.02", cDadosAdicNF: dadosAdicionais, dDataRps: todayFormatted, nCodCC: 3698183852 },
                    ServicosPrestados: [{
                        cCodServLC116: "1.05", cCodServMun: "620310002", cDescServ: descricaoServico, cRetemISS: "N", cTribServ: "01",
                        impostos: { cRetemCOFINS: "N", cRetemCSLL: "N", cRetemIRRF: "N", cRetemPIS: "N", nAliqCOFINS: 0, nAliqCSLL: 0, nAliqIRRF: 0, nAliqISS: 2.00, nAliqPIS: 0 },
                        nQtde: 1, nValUnit: invoice.total
                    }]
                };
                const osResponse = await fetch(OMIE_PROXY_FUNCTION_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: "createOS", payload: osParamPayload }) });
                if (!osResponse.ok) { let errorDataOs; try { errorDataOs = await osResponse.json(); } catch (e) { const errorTextOs = await osResponse.text(); throw new Error(errorTextOs.substring(0,200) || `Erro ${osResponse.status}`); } throw new Error(errorDataOs.message || errorDataOs.error || `Erro ${osResponse.status}`); }
                const osResult = await osResponse.json();
                if (osResult.faultstring) { throw new Error(`Omie (OS): ${osResult.faultstring}`); }
                if (osResult.nCodOS && osResult.nCodOS > 0) {
                    nCodOSCriado = osResult.nCodOS;
                    cCodIntOSCriado = osResult.cCodIntOS || cCodIntOSCriado; // Use o retornado pela Omie se disponível
                    console.log(`OS Incluída com sucesso: nCodOS=${nCodOSCriado}, cCodIntOS=${cCodIntOSCriado}`);
                    const updatedFieldsForOS = {
                        omie_nCodOS: nCodOSCriado,
                        nfStatus: `OS ${nCodOSCriado} Criada. Aguardando Faturamento Omie.`,
                        lastUpdatedNF: new Date().toISOString()
                    };
                    setInvoices(prevInvoices => prevInvoices.map(inv =>
                        inv.uniqueId === invoiceUniqueId ? { ...inv, ...updatedFieldsForOS } : inv
                    ));
                    
                    // Persistir no Firestore
                    const dataToPersistOS = { ...invoice, ...updatedFieldsForOS }; // 'invoice' é o parâmetro da função handleEmitirNF
                    if (dataToPersistOS.type === 'Fechada' && empresaId) { // 'empresaId' do estado do componente
                        await persistNFeDataToFirestore(dataToPersistOS, empresaId);
                    }
                } else { throw new Error(osResult.cDescStatus || `Erro Omie ao incluir OS. Código: ${osResult.cCodStatus || 'N/A'}`); }
            } catch (error) {
                console.error("Erro ao incluir OS via proxy:", error);
                const errorMsg = `Erro ao incluir OS para ${client.name}: ${error.message}`;
                alert(errorMsg);
                setEmissionStatus({ status: 'error', message: `Falha (OS): ${error.message.substring(0, 100)}...` });
                setEmittingNFFor(null);
                return;
            }
        } else {
            // Se já tínhamos nCodOSCriado, precisamos garantir que cCodIntOSCriado seja o correto associado a essa OS.
            // Idealmente, cCodIntOS também seria salvo junto com omie_nCodOS.
            // Por ora, se nCodOSCriado existe, mas cCodIntOS não foi definido (porque pulou a criação),
            // a API de faturarOS pode precisar apenas do nCodOS. Verifique a API Omie.
            // Se cCodIntOS for estritamente necessário para faturar uma OS existente, esta lógica precisará buscar o cCodIntOS.
            // Para este exemplo, assumiremos que o nCodOS é suficiente se o cCodIntOS não foi gerado nesta execução.
            // Se a OS foi criada em uma sessão anterior, cCodIntOS pode não estar disponível aqui.
            // O ideal é que, ao criar a OS, cCodIntOSCriado também seja salvo na fatura (Firestore/estado).
            console.log(`Tentando faturar OS existente: ${nCodOSCriado}. cCodIntOS pode não estar disponível se OS foi criada anteriormente.`);
            // Se você salvou cCodIntOS junto com omie_nCodOS no objeto invoice, use-o:
            // cCodIntOSCriado = invoice.cCodIntOSOmie || `SOLUCX-RETRY-${Date.now().toString().slice(-5)}`; // Fallback ou buscar da invoice
        }


        if (nCodOSCriado) {
            try {
                setEmissionStatus({ status: 'loading', message: `OS ${nCodOSCriado} criada/localizada. Faturando...` });
                // Se cCodIntOSCriado não foi definido (porque a OS já existia e não foi criada agora),
                // pode ser necessário usar um cCodIntOS conhecido ou um placeholder se a API de faturamento permitir
                // faturar apenas com nCodOS. A documentação da Omie "FaturarOS" geralmente requer ambos.
                // Para robustez, se cCodIntOS não foi setado (OS pré-existente sem cCodIntOS no 'invoice'),
                // esta etapa pode falhar ou precisar de um cCodIntOS associado.
                // Por ora, o cCodIntOSCriado da tentativa de criação (mesmo que vazia) é usado.
                // Se a OS já existia, o cCodIntOS usado na sua criação original seria o ideal.
                // A API de "FaturarOS" da Omie geralmente usa o cCodIntOS da OS e o nCodOS.

                const faturarPayload = {
                     nCodOS: nCodOSCriado
                    // Se a API 'FaturarOS' da Omie EXIGE cCodIntOS, e ele não está disponível aqui para uma OS pré-existente,
                    // será necessário obter o cCodIntOS da OS primeiro, ou garantir que ele seja salvo com a fatura.
                    // Para este exemplo, se cCodIntOSCriado estiver vazio (porque pulou a criação da OS),
                    // a API de faturar pode precisar dele.
                    // A API Omie `FaturarOS` geralmente pede `cCodIntOS` e `nCodOS`.
                    // Se `cCodIntOSCriado` não foi obtido na criação (porque a OS já existia),
                    // e não está salvo na `invoice`, esta chamada pode falhar.
                    // O ideal é que `invoice.cCodIntOSOmie` (ou similar) seja salvo.
                    // Se não, a API pode inferir ou você pode precisar listar a OS para obter o cCodIntOS.
                };
                if (cCodIntOSCriado) { // Adiciona cCodIntOS apenas se ele foi definido (nova OS ou recuperado)
                    faturarPayload.cCodIntOS = cCodIntOSCriado;
                } else {
                    // Se cCodIntOS não está disponível, a chamada para faturar pode não funcionar como esperado.
                    // Omie normalmente espera cCodIntOS para identificar a OS a ser faturada junto com nCodOS.
                    console.warn(`cCodIntOS não está disponível para faturar OS ${nCodOSCriado}. A API Omie pode exigir este campo.`);
                    // Poderia tentar buscar o cCodIntOS aqui se necessário, ou alertar o usuário.
                    // Por enquanto, prosseguirá sem ele se não estiver definido, mas pode causar falha.
                }


                const faturarResponse = await fetch(OMIE_PROXY_FUNCTION_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: "faturarOS", payload: faturarPayload })
                });
                if (!faturarResponse.ok) {
                    let errorDataFaturar; try { errorDataFaturar = await faturarResponse.json(); } catch (e) { const errorTextFaturar = await faturarResponse.text(); console.error("Raw error from faturarOS:", errorTextFaturar); throw new Error(errorTextFaturar.substring(0,200) || `Erro ${faturarResponse.status} ao faturar OS.`);}
                    // A mensagem "A NFS-e foi faturada com sucesso." que você mencionou no problema
                    // pode vir de errorDataFaturar.cDescStatus mesmo com response não ok (ex: status 4xx, 5xx com corpo JSON).
                    // Vamos verificar se a resposta, mesmo com erro HTTP, indica que já foi faturado.
                    if (errorDataFaturar && ( (errorDataFaturar.faultstring && errorDataFaturar.faultstring.includes("NFSe já enviada para a Prefeitura")) || (errorDataFaturar.cDescStatus && errorDataFaturar.cDescStatus.includes("NFSe já enviada para a Prefeitura")) )) {
                        const alreadySentMsg = `OS ${nCodOSCriado}: NF-e já havia sido enviada para a prefeitura. Verifique o status.`;
                        alert(alreadySentMsg);
                        setEmissionStatus({ status: 'warning', message: alreadySentMsg });
                        setInvoices(prevInvoices => prevInvoices.map(inv =>
                            inv.uniqueId === invoiceUniqueId
                            ? { ...inv, nfStatus: `NF-e já enviada à prefeitura. Verifique o status.`, omie_nCodOS: nCodOSCriado } // Garante que omie_nCodOS está lá
                            : inv
                        ));
                        setEmittingNFFor(null);
                        return;
                    }
                    throw new Error(errorDataFaturar.message || errorDataFaturar.error || errorDataFaturar.faultstring || `Erro ${faturarResponse.status} ao faturar OS.`);
                }
                const faturarResult = await faturarResponse.json();

                // Se a API Omie responder com 2xx mas tiver um faultstring (comum em SOAP via JSON)
                if (faturarResult.faultstring) {
                     if (faturarResult.faultstring.includes("NFSe já enviada para a Prefeitura")) {
                        const alreadySentMsg = `OS ${nCodOSCriado}: NF-e já havia sido enviada para a prefeitura. Verifique o status.`;
                        alert(alreadySentMsg);
// ... alert(alreadySentMsg);
setEmissionStatus({ status: 'warning', message: alreadySentMsg });

const updatedFieldsAlreadySent = {
    omie_nCodOS: nCodOSCriado, // Garante que está lá
    nfStatus: `NF-e já enviada à prefeitura. Verifique o status.`,
    lastUpdatedNF: new Date().toISOString()
};
setInvoices(prevInvoices => prevInvoices.map(inv =>
    inv.uniqueId === invoiceUniqueId ? { ...inv, ...updatedFieldsAlreadySent } : inv
));

// Persistir no Firestore
const dataToPersistAlreadySent = { ...invoice, ...updatedFieldsAlreadySent };
if (dataToPersistAlreadySent.type === 'Fechada' && empresaId) {
    await persistNFeDataToFirestore(dataToPersistAlreadySent, empresaId);
}
                        setEmittingNFFor(null);
                        return;
                    }
                    throw new Error(`Omie (Faturar OS): ${faturarResult.faultstring}`);
                }

                if (faturarResult.nCodNF && faturarResult.nCodNF > 0) {
                    const successMsg = `OS ${nCodOSCriado} FATURADA! NF Omie ${faturarResult.nCodNF} registrada. Solicitação de NFS-e ENVIADA PARA PREFEITURA. Status Omie: ${faturarResult.cStatus || 'Enviado'}. Aguarde autorização e verifique o status.`;
                    console.log("OS Faturada, aguardando prefeitura:", faturarResult);
                    alert(successMsg); // Mensagem clara para o usuário
// ... alert(successMsg);
setEmissionStatus({ status: 'success', message: `NF Omie ${faturarResult.nCodNF} enviada à prefeitura. Verifique status final.` });

const updatedFieldsForOmieNF = {
    omie_nCodOS: nCodOSCriado, // Garante que está lá
    nCodOmieNF: faturarResult.nCodNF,
    nfStatus: `NF Omie ${faturarResult.nCodNF} enviada. Aguardando Pref.`,
    nfStatusOmie: faturarResult.cStatus || null,
    nNumeroNFSe: null, cCodigoVerifNFSe: null, nfSituacao: null, // Limpa dados antigos da prefeitura
    lastUpdatedNF: new Date().toISOString()
};
setInvoices(prevInvoices => prevInvoices.map(inv =>
    inv.uniqueId === invoiceUniqueId ? { ...inv, ...updatedFieldsForOmieNF } : inv
));

// Persistir no Firestore
const dataToPersistOmieNF = { ...invoice, ...updatedFieldsForOmieNF };
if (dataToPersistOmieNF.type === 'Fechada' && empresaId) {
    await persistNFeDataToFirestore(dataToPersistOmieNF, empresaId);
}
                } else {
                     // Cenário onde nCodNF não veio, mas não é um erro "já enviado" explícito
                     const descStatus = faturarResult.cDescStatus || `Resposta inesperada ao faturar OS. Status Omie: ${faturarResult.cStatus || 'N/A'}`;
                     // Se a descrição indica que foi faturado com sucesso e está aguardando prefeitura (como no problema original)
                     if (descStatus.toLowerCase().includes("nfs-e foi faturada com sucesso") && descStatus.toLowerCase().includes("assim que autorizada")) {
                        const pendingMsg = `OS ${nCodOSCriado}: ${descStatus}. A NFS-e foi solicitada à prefeitura. Verifique o status em breve.`;
                        alert(pendingMsg);
// ... alert(pendingMsg);
setEmissionStatus({ status: 'success', message: `OS ${nCodOSCriado}: NFS-e solicitada. Verifique status.` });

const updatedFieldsPendingAuth = {
    omie_nCodOS: nCodOSCriado,
    nfStatus: `NFS-e solicitada à prefeitura. Verifique status. (OS: ${nCodOSCriado})`,
    nfStatusOmie: faturarResult.cStatus || 'Aguardando Prefeitura',
    lastUpdatedNF: new Date().toISOString()
};
setInvoices(prevInvoices => prevInvoices.map(inv =>
    inv.uniqueId === invoiceUniqueId ? { ...inv, ...updatedFieldsPendingAuth } : inv
));

// Persistir no Firestore
const dataToPersistPendingAuth = { ...invoice, ...updatedFieldsPendingAuth };
if (dataToPersistPendingAuth.type === 'Fechada' && empresaId) {
    await persistNFeDataToFirestore(dataToPersistPendingAuth, empresaId);
}
                     } else {
                        // Outros casos onde nCodNF não veio
                        throw new Error(descStatus);
                     }
                }
            } catch (error) {
                console.error("Erro ao faturar OS via proxy:", error);
                // A mensagem de "FALHOU AO FATURAR" vem daqui se for um erro real
                const finalErrorMsg = `OS ${nCodOSCriado} foi criada/localizada, mas FALHOU AO FATURAR: ${error.message}`;
                alert(finalErrorMsg);
                setEmissionStatus({ status: 'error', message: `OS ${nCodOSCriado}: Falha Faturamento: ${error.message.substring(0, 80)}...` });
                 setInvoices(prevInvoices => prevInvoices.map(inv =>
                    inv.uniqueId === invoiceUniqueId
                    ? { ...inv, nfStatus: `OS ${nCodOSCriado}: Falha ao faturar. (${error.message.substring(0,30)}...)`, omie_nCodOS: nCodOSCriado }
                    : inv
                ));
            } finally {
                setEmittingNFFor(null);
            }
        } else {
            // Se nCodOSCriado não existe nem pôde ser criado
            setEmittingNFFor(null);
            if (!emissionStatus.message.includes("Falha (OS)")) { // Evita sobrescrever erro anterior de OS
                setEmissionStatus({ status: 'error', message: 'Não foi possível criar ou identificar a OS para faturamento.' });
            }
        }
    };

    const handleCheckNFStatus = async (invoiceToUpdate) => {
        if (!invoiceToUpdate || !invoiceToUpdate.omie_nCodOS) {
            const errorMsg = "Código da OS (omie_nCodOS) não encontrado para esta fatura. Não é possível verificar o status.";
            alert(errorMsg);
            setNfCheckStatus({ status: 'error', message: errorMsg });
            return;
        }

        const invoiceUniqueId = invoiceToUpdate.uniqueId;
        setCheckingNFStatusFor(invoiceUniqueId);
        setNfCheckStatus({ status: 'loading', message: `Verificando NF-e para OS ${invoiceToUpdate.omie_nCodOS}...` });

        try {
            const response = await fetch(OMIE_PROXY_FUNCTION_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: "listarNFSEs",
                    payload: { nPagina: 1, nRegPorPagina: 1, nCodigoOS: invoiceToUpdate.omie_nCodOS }
                })
            });

            if (!response.ok) {
                let errorData; try { errorData = await response.json(); } catch (e) { const errorText = await response.text(); throw new Error(errorText.substring(0,200) || `Erro ${response.status}`);}
                throw new Error(errorData.message || errorData.error || errorData.faultstring || `Erro ${response.status} ao buscar NFSEs.`);
            }
            const result = await response.json();

            if (result.faultstring) {
                throw new Error(`Omie (ListarNFSEs): ${result.faultstring}`);
            }

            if (result.nfseEncontradas && result.nfseEncontradas.length > 0) {
                const nfseData = result.nfseEncontradas[0];
                // Usar 'Cabecalho' com 'C' maiúsculo conforme o JSON de exemplo do usuário
                const nNumeroNFSe = nfseData.Cabecalho?.nNumeroNFSe;
                const cCodigoVerifNFSe = nfseData.Cabecalho?.cCodigoVerifNFSe;
                const cStatusNFSeOmie = nfseData.Cabecalho?.cStatusNFSe; // Status direto da Omie/Prefeitura (ex: "F", "C")
                const nCodOmieNF = nfseData.Cabecalho?.nCodNF; // Código da NF na Omie

                // Tenta pegar uma situação mais detalhada se disponível, senão usa o status mapeado
                const situacaoNFSePrefeitura = nfseData.detalhes?.cSituacaoNFSe || mapOmieNFSeStatusToUserFriendly(cStatusNFSeOmie);
                const mensagemAdicional = nfseData.detalhes?.cMensagem || "";

                if (nNumeroNFSe && cCodigoVerifNFSe) {
                    const successMsg = `NF-e ${nNumeroNFSe} (${situacaoNFSePrefeitura}) encontrada e atualizada.`;
                    setNfCheckStatus({ status: 'success', message: successMsg });
                    alert(successMsg);
                    const updatedFieldsNFeFound = {
                        nNumeroNFSe,
                        cCodigoVerifNFSe,
                        nCodOmieNF: nCodOmieNF || invoiceToUpdate.nCodOmieNF, // nCodOmieNF da resposta da API
                        nfStatus: `NF-e: <span class="math-inline">\{nNumeroNFSe\} \(</span>{situacaoNFSePrefeitura})`,
                        nfSituacao: situacaoNFSePrefeitura,
                        nfStatusOmie: cStatusNFSeOmie,
                        lastUpdatedNF: new Date().toISOString()
                    };
                    setInvoices(prevInvoices => prevInvoices.map(inv =>
                        inv.uniqueId === invoiceUniqueId ? { ...inv, ...updatedFieldsNFeFound } : inv
                    ));
                    
                    // Persistir no Firestore
                    // 'invoiceToUpdate' é o parâmetro da função handleCheckNFStatus
                    const dataToPersistNFeFound = { ...invoiceToUpdate, ...updatedFieldsNFeFound };
                    if (dataToPersistNFeFound.type === 'Fechada' && empresaId) {
                        await persistNFeDataToFirestore(dataToPersistNFeFound, empresaId);
                    }
                } else {
                    // Dados da NF-e não encontrados na resposta, mesmo com nfseEncontradas[0] existindo
                    const errorDetail = `NF-e emitida, mas dados (Nº ${nNumeroNFSe}, Cód.Verif ${cCodigoVerifNFSe}) incompletos na resposta. Situação: ${situacaoNFSePrefeitura}. ${mensagemAdicional}`;
                    console.warn("handleCheckNFStatus - Dados incompletos:", nfseData.Cabecalho);
                    throw new Error(errorDetail);
                }
            } else {
                // Nenhuma NF-e encontrada para a OS
                const statusOmieMsg = result.nfseCabecMsg || "Nenhuma NF-e encontrada para esta OS. Pode estar aguardando processamento pela prefeitura ou houve um problema na emissão.";
                setNfCheckStatus({ status: 'warning', message: `${statusOmieMsg} (OS: ${invoiceToUpdate.omie_nCodOS})` });
                alert(`${statusOmieMsg} (OS: ${invoiceToUpdate.omie_nCodOS})`);
                const updatedFieldsNoNFe = {
                    nfStatus: `Aguardando Pref. (OS: ${invoiceToUpdate.omie_nCodOS}). ${statusOmieMsg.substring(0,50)}...`,
                    nfSituacao: 'Pendente Prefeitura', // Ou um status mais apropriado
                    nfStatusOmie: null, // Limpa se não achou NF na prefeitura
                    nNumeroNFSe: null, // Garante que está limpo
                    cCodigoVerifNFSe: null, // Garante que está limpo
                    lastUpdatedNF: new Date().toISOString()
                };
                setInvoices(prevInvoices => prevInvoices.map(inv =>
                    inv.uniqueId === invoiceUniqueId ? { ...inv, ...updatedFieldsNoNFe } : inv
                ));
                
                // Persistir no Firestore
                const dataToPersistNoNFe = { ...invoiceToUpdate, ...updatedFieldsNoNFe };
                if (dataToPersistNoNFe.type === 'Fechada' && empresaId) {
                    await persistNFeDataToFirestore(dataToPersistNoNFe, empresaId);
                }
            }
        } catch (error) {
            console.error("Erro ao verificar status da NF-e:", error);
            const errorMsg = `Erro ao verificar NF-e (OS ${invoiceToUpdate.omie_nCodOS}): ${error.message}`;
            setNfCheckStatus({ status: 'error', message: errorMsg.substring(0,150) + "..." });
            alert(errorMsg);
             setInvoices(prevInvoices => prevInvoices.map(inv =>
                inv.uniqueId === invoiceUniqueId
                ? { ...inv, nfStatus: `Falha ao verificar NF-e (OS: ${invoiceToUpdate.omie_nCodOS}).` }
                : inv
            ));
        } finally {
            setCheckingNFStatusFor(null);
        }
    };

    // --- Batch Actions ---
    const handleToggleSelectInvoice = (invoiceUniqueId) => {
        setSelectedInvoices(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(invoiceUniqueId)) {
                newSelected.delete(invoiceUniqueId);
            } else {
                newSelected.add(invoiceUniqueId);
            }
            return newSelected;
        });
    };

    const handleEmitirSelecionadas = async () => {
        if (selectedInvoices.size === 0) {
            alert("Nenhuma fatura selecionada para emissão.");
            return;
        }
        alert(`Iniciando emissão para ${selectedInvoices.size} fatura(s) selecionada(s).\nEsta funcionalidade está em desenvolvimento.\nO sistema tentará emitir uma por uma. Aguarde os alertas individuais.`);

        // Desabilitar botões de lote durante o processo
        // Adicionar feedback visual para cada fatura processada

        for (const invoiceUniqueId of selectedInvoices) {
            const invoiceToEmit = invoices.find(inv => inv.uniqueId === invoiceUniqueId);
            if (invoiceToEmit) {
                // Verificar se a fatura é elegível para emissão (canEmit logic)
                const canEmit = (invoiceToEmit.type === 'Fechada' || invoiceToEmit.type === 'Setup') &&
                                invoiceToEmit.total > 0 &&
                                !invoiceToEmit.nNumeroNFSe && // Não tem NF da prefeitura ainda
                                !invoiceToEmit.omie_nCodOS; // E não tem OS criada (ou se tiver, faturar direto) - Lógica do `canEmit` pode precisar de refinamento

                if (canEmit || invoiceToEmit.omie_nCodOS) { // Se tem OS, tenta faturar; se não, cria OS e fatura
                    console.log(`Processando em lote: Emissão de NF para ${invoiceToEmit.clientName} (${invoiceUniqueId})`);
                    setEmissionStatus({status: 'idle', message: ''}); // Limpa status anterior
                    await handleEmitirNF(invoiceToEmit); // Aguarda cada emissão para não sobrecarregar
                    // Poderia adicionar um pequeno delay aqui se necessário
                } else {
                    alert(`Fatura para ${invoiceToEmit.clientName} (${invoiceUniqueId}) não é elegível para emissão neste momento (Valor: ${invoiceToEmit.total}, Status NF: ${invoiceToEmit.nfStatus}, OS: ${invoiceToEmit.omie_nCodOS}).`);
                }
            }
        }
        alert("Processamento em lote de emissão concluído (ou tentativas). Verifique o status individual das faturas.");
        setSelectedInvoices(new Set()); // Limpar seleção após o lote
    };

    const handleVerificarStatusSelecionadas = async () => {
        if (selectedInvoices.size === 0) {
            alert("Nenhuma fatura selecionada para verificação de status.");
            return;
        }
        alert(`Iniciando verificação de status para ${selectedInvoices.size} fatura(s) selecionada(s).\nEsta funcionalidade está em desenvolvimento.\nAguarde os alertas individuais.`);

        for (const invoiceUniqueId of selectedInvoices) {
            const invoiceToCheck = invoices.find(inv => inv.uniqueId === invoiceUniqueId);
            if (invoiceToCheck && invoiceToCheck.omie_nCodOS) { // Só verifica se tem OS
                console.log(`Processando em lote: Verificação de Status para ${invoiceToCheck.clientName} (${invoiceUniqueId})`);
                setNfCheckStatus({status: 'idle', message: ''}); // Limpa status anterior
                await handleCheckNFStatus(invoiceToCheck);
                // Poderia adicionar um pequeno delay aqui
            } else if (invoiceToCheck) {
                 alert(`Fatura para ${invoiceToCheck.clientName} (${invoiceUniqueId}) não possui OS registrada para verificação de status.`);
            }
        }
        alert("Processamento em lote de verificação de status concluído (ou tentativas). Verifique o status individual das faturas.");
        setSelectedInvoices(new Set()); // Limpar seleção
    };
    // --- End Batch Actions ---


    if (loading && invoices.length === 0) return <div className="loading-message">Carregando faturas...</div>;
    if (!user) return <div className="error-message">Usuário não autenticado. Faça login novamente.</div>;
    if (!empresaId && !loading) return <div className="error-message">Não foi possível identificar a empresa do usuário. Verifique seu cadastro.</div>;
    if (!issuerInfo.empnome && !loading) return <div className="error-message">Informações da empresa emissora não encontradas.</div>;

    return (
        <div className="faturas-wrapper">
            <div className="faturas-header">
                <h1>Faturas</h1>
                <p>Visão geral das faturas mensais, provisórias e de setup por cliente.</p>
                 {(emissionStatus.status !== 'idle' && emissionStatus.status !== 'loading') && (
                     <div className={`emission-status-banner status-${emissionStatus.status}`}>
                         {emissionStatus.status === 'success' ? <CheckCircle size={16} /> : emissionStatus.status === 'warning' ? <AlertTriangle size={16} /> : <AlertTriangle size={16} />}
                         <span>{emissionStatus.message}</span>
                         <button onClick={() => setEmissionStatus({ status: 'idle', message: '' })} title="Fechar aviso"><X size={14} /></button>
                     </div>
                 )}
                 {(nfCheckStatus.status !== 'idle' && nfCheckStatus.status !== 'loading') && (
                     <div className={`emission-status-banner status-${nfCheckStatus.status}`}>
                         {nfCheckStatus.status === 'success' ? <CheckCircle size={16} /> : nfCheckStatus.status === 'warning' ? <AlertTriangle size={16} /> : <AlertTriangle size={16} />}
                         <span>{nfCheckStatus.message}</span>
                         <button onClick={() => setNfCheckStatus({ status: 'idle', message: '' })} title="Fechar aviso"><X size={14} /></button>
                     </div>
                 )}
            </div>

            {/* Batch Action Buttons */}
            <div className="batch-actions-container" style={{ margin: '10px 0', padding: '10px', background: '#f0f0f0', borderRadius: '4px', display: 'flex', gap: '10px' }}>
                <button
                    onClick={handleEmitirSelecionadas}
                    disabled={selectedInvoices.size === 0 || emittingNFFor || checkingNFStatusFor} // Disable if any single action is in progress
                    className="action-button"
                    title="Emitir NF-e para todas as faturas selecionadas (que são elegíveis)"
                >
                    <FileText size={16} style={{ marginRight: '5px' }} />
                    Emitir Selecionadas ({selectedInvoices.size})
                </button>
                <button
                    onClick={handleVerificarStatusSelecionadas}
                    disabled={selectedInvoices.size === 0 || emittingNFFor || checkingNFStatusFor}
                    className="action-button"
                    title="Verificar status da NF-e na prefeitura para todas as faturas selecionadas (que possuem OS)"
                >
                    <RefreshCw size={16} style={{ marginRight: '5px' }} />
                    Verificar Status Selecionadas ({selectedInvoices.size})
                </button>
            </div>


            {(clients.length > 0 || !loading) ? (
                <div className="faturas-card-container">
                    <div className="faturas-card-header">
                        <div className="faturas-search">
                            <Filter size={18}/>
                            <input type="text" placeholder="Filtrar por Nome do Cliente..." value={filterClient} onChange={handleClientFilterChange}/>
                        </div>
                        <select value={filterStatus} onChange={handleStatusFilterChange} >
                            <option value="">Todos Status</option>
                            <option value="Fechada">Fechada</option>
                            <option value="Provisória">Provisória</option>
                            <option value="Setup">Setup</option>
                            <option value="Erro">Erro</option>
                        </select>
                        <select value={filterMonth} onChange={handleMonthFilterChange} >
                            <option value="">Todas Competências</option>
                            {uniqueMonths.map(month => (<option key={month} value={month}>{month}</option>))}
                        </select>
                    </div>

                    {paginatedInvoices.length > 0 ? (
                        <table className="faturas-table">
                            <thead>
                                <tr>
                                    <th style={{width: '30px', textAlign: 'center'}}>
                                        <input
                                            type="checkbox"
                                            title="Selecionar todas nesta página"
                                            onChange={(e) => {
                                                const newSelected = new Set(selectedInvoices);
                                                if (e.target.checked) {
                                                    paginatedInvoices.forEach(inv => newSelected.add(inv.uniqueId));
                                                } else {
                                                    paginatedInvoices.forEach(inv => newSelected.delete(inv.uniqueId));
                                                }
                                                setSelectedInvoices(newSelected);
                                            }}
                                            // Check if all on current page are selected
                                            checked={paginatedInvoices.length > 0 && paginatedInvoices.every(inv => selectedInvoices.has(inv.uniqueId))}
                                        />
                                    </th>
                                    <th>Cliente</th><th>Competência</th><th>Período</th>
                                    <th>Status Fatura</th><th>NF-e Info / Status</th><th>Valor Total</th><th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedInvoices.map((inv) => { // Removed index i, use inv.uniqueId
                                    const isEmitting = emittingNFFor === inv.uniqueId;
                                    const isCheckingNF = checkingNFStatusFor === inv.uniqueId;
                                    // Updated canEmit logic: Eligible if Fechada/Setup, total > 0, and no NF da Prefeitura yet.
                                    // If omie_nCodOS exists, it means OS was created, so faturamento é o próximo passo.
                                    // Se não tem omie_nCodOS, então é criação de OS + Faturamento.
                                    const canEmit = (inv.type === 'Fechada' || inv.type === 'Setup') &&
                                                    inv.total > 0 &&
                                                    !inv.nNumeroNFSe; // Simplificado: pode emitir se não tem NF da prefeitura ainda.
                                                                      // A lógica interna de handleEmitirNF decidirá se cria OS ou só fatura.

                                    const canCheckStatus = inv.omie_nCodOS && !inv.nNumeroNFSe; // Pode verificar se tem OS e não tem NF ainda.

                                    return (
                                    <tr key={inv.uniqueId} className={inv.type === 'Provisória' ? 'provisional-row' : inv.type === 'Setup' ? 'setup-row' : inv.type === 'Erro' ? 'error-row' : ''}>
                                        <td style={{textAlign: 'center'}}>
                                            <input
                                                type="checkbox"
                                                checked={selectedInvoices.has(inv.uniqueId)}
                                                onChange={() => handleToggleSelectInvoice(inv.uniqueId)}
                                                disabled={isEmitting || isCheckingNF}
                                            />
                                        </td>
                                        <td>{inv.clientName || 'Cliente Desconhecido'}</td>
                                        <td>{inv.monthLabel || 'N/A'}</td>
                                        <td>{inv.period}</td>
                                        <td><span className={`status status-${inv.type?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || 'unknown'}`}>{inv.type || 'Desconhecido'}</span></td>
                                        <td className="nf-status-cell">
                                            {inv.nNumeroNFSe && inv.cCodigoVerifNFSe ? (
                                                <div className="nf-info">
                                                    <span>NF-e: {inv.nNumeroNFSe}</span>
                                                    <span className="nf-situacao">({inv.nfSituacao || mapOmieNFSeStatusToUserFriendly(inv.nfStatusOmie) || 'Status Desconhecido'})</span>
                                                    <a href={`https://notajoseense.sjc.sp.gov.br/notafiscal-ws/servico/notafiscal/autenticacao/cpfCnpj/13537122000163/inscricaoMunicipal/330845/numeroNota/${inv.nNumeroNFSe}/codigoVerificacao/${inv.cCodigoVerifNFSe}`}
                                                       target="_blank" rel="noopener noreferrer" className="action-button" title="Ver PDF da NF-e na Prefeitura">
                                                        <ExternalLink size={16} />
                                                    </a>
                                                </div>
                                            ) : inv.omie_nCodOS ? (
                                                <div className="nf-info pending">
                                                   <span title={`OS Omie: ${inv.omie_nCodOS} / NF Omie: ${inv.nCodOmieNF || 'N/A'}`}>{inv.nfStatus || `OS ${inv.omie_nCodOS} no Omie`}</span>
                                                    <button
                                                        onClick={() => handleCheckNFStatus(inv)}
                                                        className={`action-button refresh-nf ${isCheckingNF ? 'loading' : ''}`}
                                                        title="Verificar Status da NF-e na Prefeitura"
                                                        disabled={!canCheckStatus || isCheckingNF || isEmitting}
                                                    >
                                                        {isCheckingNF ? <Loader2 size={16} className="spin-icon" /> : <RefreshCw size={16} />}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span>{inv.nfStatus || 'Não emitida'}</span>
                                            )}
                                        </td>
                                        <td>{formatCurrency(inv.total)}</td>
                                        <td className="faturas-actions">
                                            <button
                                                onClick={() => { setSelectedInv(inv); setModalOpen(true); }}
                                                className="action-button"
                                                title="Ver Detalhes da Fatura"
                                                disabled={isEmitting || isCheckingNF}
                                            >
                                                <List size={18} />
                                            </button>
                                            <button
                                                 onClick={() => handleEmitirNF(inv)}
                                                 className={`action-button emit-nf ${isEmitting ? 'loading' : ''} ${!canEmit ? 'disabled' : ''}`}
                                                 title={isEmitting ? emissionStatus.message : canEmit ? "Emitir Nota Fiscal (Omie)" : (inv.nNumeroNFSe ? "NF-e já emitida e autorizada" : (inv.omie_nCodOS && !inv.nNumeroNFSe ? "OS já criada, aguardando autorização ou erro no faturamento anterior. Tente 'Emitir' para faturar ou 'Verificar Status'." : "Emissão não disponível (verifique valor, tipo ou status NF)"))}
                                                 disabled={!canEmit || isEmitting || isCheckingNF}
                                             >
                                                 {isEmitting ? <Loader2 size={18} className="spin-icon" /> : <FileText size={18} />}
                                             </button>
                                        </td>
                                    </tr>
                                )})}
                            </tbody>
                        </table>
                     ) : (
                         !loading && <div className="info-message">Nenhuma fatura encontrada para os filtros aplicados.</div>
                     )}

                    {filteredInvoices.length > itemsPerPage && (
                        <div className="pagination-controls">
                            <span>Página {currentPage} de {totalPages} ({filteredInvoices.length} resultados)</span>
                            <div>
                                <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} aria-label="Página Anterior"><ChevronLeft size={16} /> Anterior</button>
                                <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} aria-label="Próxima Página">Próxima <ChevronRight size={16} /></button>
                            </div>
                        </div>
                     )}
                </div>
            ) : (
                 !loading && <div className="info-message">Nenhum cliente encontrado ou nenhuma fatura gerada ainda.</div>
            )}

             {modalOpen && selectedInv && (
                <div className="modal-overlay" onClick={() => setModalOpen(false)}>
                  <div className="modal-wrapper" ref={modalRef} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="modal-title-h2">
                    <button className="modal-close" onClick={() => setModalOpen(false)} aria-label="Fechar"><X size={20} /></button>
                    <header className="modal-header">
                       <img src={logo} alt="Logo da Empresa" className="modal-logo" />
                       <div className="modal-title">
                         <h2 id="modal-title-h2">Extrato Detalhado</h2>
                         <span className="modal-subtitle">{selectedInv.clientName} — {selectedInv.monthLabel || selectedInv.period}</span>
                       </div>
                       <div className="modal-actions">
                         <button className="btn-download" onClick={downloadPDF}><Download size={16} /><span>Baixar PDF</span></button>
                       </div>
                    </header>
                    <section className="modal-section modal-info-grid">
                      <div className="info-card">
                        <div className="info-card-icon"><Building size={24} /></div>
                        <div className="info-card-content">
                          <label>Empresa Emissora</label><p>{issuerInfo.empnome || 'N/A'}</p>
                          {issuerInfo.empcnpj && (<p className="cnpj">{formatCNPJ(issuerInfo.empcnpj)}</p>)}
                        </div>
                      </div>
                      <div className="info-card">
                         <div className="info-card-icon"><User size={24} /></div>
                         <div className="info-card-content">
                           <label>Cliente</label><p>{selectedInv.clientName}</p>
                          {selectedInv.clientCnpj && (<p className="cnpj">{selectedInv.clientCnpj}</p>)}
                         </div>
                       </div>
                       {/* NF-e Info in Modal */}
                       {selectedInv.nNumeroNFSe && selectedInv.cCodigoVerifNFSe && (
                           <div className="info-card nf-info-modal">
                               <div className="info-card-icon"><FileText size={24} /></div>
                               <div className="info-card-content">
                                   <label>Nota Fiscal Eletrônica</label>
                                   <p>Número: {selectedInv.nNumeroNFSe}</p>
                                   <p>Situação: {selectedInv.nfSituacao || mapOmieNFSeStatusToUserFriendly(selectedInv.nfStatusOmie) || 'Desconhecida'}</p>
                                   <p>
                                       <a href={`https://notajoseense.sjc.sp.gov.br/notafiscal-ws/servico/notafiscal/autenticacao/cpfCnpj/13537122000163/inscricaoMunicipal/330845/numeroNota/${selectedInv.nNumeroNFSe}/codigoVerificacao/${selectedInv.cCodigoVerifNFSe}`}
                                          target="_blank" rel="noopener noreferrer" className="link-pdf-modal">
                                            Verificar PDF da NF-e <ExternalLink size={14} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />
                                       </a>
                                   </p>
                               </div>
                           </div>
                       )}
                         {selectedInv.omie_nCodOS && (
                           <div className="info-card">
                               <div className="info-card-icon"><Copy size={24} /></div>
                               <div className="info-card-content">
                                   <label>Informações Omie</label>
                                   <p>Cód. OS: {selectedInv.omie_nCodOS}</p>
                                   {selectedInv.nCodOmieNF && <p>Cód. NF Omie: {selectedInv.nCodOmieNF}</p>}
                                   {selectedInv.nfStatusOmie && <p>Status Omie: {selectedInv.nfStatusOmie} ({mapOmieNFSeStatusToUserFriendly(selectedInv.nfStatusOmie)})</p>}
                               </div>
                           </div>
                        )}
                    </section>
                    {selectedInv.type !== 'Setup' && (
                       <section className="modal-section modal-calc">
                         <h3 className="section-title">Resumo de Consumo e Custos (CX Platform)</h3>
                         <div className="calc-line">
                           <span>{selectedInv.cx_VolumeContratado > 0 ? 'Volume Contratado' : 'Consumo Realizado'}</span>
                           <strong>
                              {selectedInv.cx_VolumeContratado > 0
                                  ? `${formatCount(selectedInv.consumido)} / ${formatCount(selectedInv.cx_VolumeContratado)} u`
                                  : `${formatCount(selectedInv.consumido)} u`
                              }
                             {selectedInv.cx_ticket > 0 && <span className="calc-value"> | Ticket: {formatCurrency(selectedInv.cx_ticket)}</span>}
                           </strong>
                         </div>
                        {selectedInv.excedenteExiste && (
                           <div className="calc-line excedente">
                             <span>Excedente Total</span>
                             <strong className="value-excedente">
                               {formatCount(selectedInv.contagemExcedenteTotal)} u
                               {selectedInv.valorExcedenteTotal > 0 && <span className="calc-value"> | Custo: {formatCurrency(selectedInv.valorExcedenteTotal)}</span>}
                             </strong>
                           </div>
                         )}
                         {selectedInv.excedenteExiste && selectedInv.produtosComExcedente?.length > 0 && (
                           <div className="excedente-details">
                            <small>Detalhes do Excedente:</small>
                            <ul>
                                {selectedInv.produtosComExcedente.map((p, idx) => (
                                    <li key={idx}>
                                        <small>
                                            {p.nome}: +{formatCount(p.contagem)} u
                                            {p.valorCobrado > 0 ? ` (${formatCurrency(p.valorCobrado)})` : ' (sem custo adicional)'}
                                         </small>
                                    </li>
                                ))}
                            </ul>
                           </div>
                         )}
                         {selectedInv.cx_VolumeContratado === 0 && selectedInv.consumido === 0 && !selectedInv.excedenteExiste && (
                             <p className="info-message-small">Dados de consumo CX Platform não aplicáveis ou não encontrados para este período.</p>
                         )}
                       </section>
                     )}
                    <section className="modal-section modal-items">
                       <h3 className="section-title">Itens Faturados</h3>
                       <div className="table-container">
                         <table>
                           <thead><tr><th>Descrição</th><th className="th-value">Valor</th></tr></thead>
                           <tbody>
                             {selectedInv.details.map((d, i) => (
                                 <tr key={i} className={d.isExcedente ? 'row-excedente' : d.isError ? 'row-error' : d.isWarning ? 'row-warning' : d.isInfo ? 'row-info' : ''}>
                                     <td>{d.desc}</td>
                                     <td className="td-value">{d.value}</td>
                                 </tr>
                             ))}
                             {selectedInv.details.length === 0 && (
                                 <tr><td colSpan="2" style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>
                                     Nenhum item detalhado encontrado para esta fatura.
                                 </td></tr>
                             )}
                           </tbody>
                         </table>
                       </div>
                    </section>
                    <footer className="modal-footer">
                      <div className="footer-total">
                        <span>Total {selectedInv.type === 'Provisória' ? 'Provisório' : 'a Pagar'}</span>
                        <strong>{formatCurrency(selectedInv.total)}</strong>
                       </div>
                    </footer>
                  </div>
                </div>
             )}
        </div>
    );
};

export default Faturas;